import React, { useState, useEffect } from 'react';
import { 
  Form, Button, Alert, Spinner, 
  Card, Row, Col, InputGroup 
} from 'react-bootstrap';
import { 
  FaUser, FaEnvelope, FaPhone, 
  FaSave, FaArrowLeft, FaShieldAlt 
} from 'react-icons/fa';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const AddEditUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  
  const [user, setUser] = useState({
    name: '',
    email: '',
    phone: '',
    authMethod: 'email',
    usertype: 'customer'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isEdit) {
      const fetchUser = async () => {
        try {
          setLoading(true);
          const token = localStorage.getItem('adminToken');
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/users/${id}`, {
            headers: { 'x-auth-token': token }
          });
          setUser(response.data);
        } catch (err) {
          console.error('Error fetching user:', err);
          setError(err.response?.data?.error || 'Failed to load user');
        } finally {
          setLoading(false);
        }
      };
      fetchUser();
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUser(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('adminToken');
      
      if (isEdit) {
        await axios.put(`${process.env.REACT_APP_API_URL}/admin/users/${id}`,
          user,
          { headers: { 'x-auth-token': token } }
        );
        toast.success('User updated successfully');
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/admin/users`,
          user,
          { headers: { 'x-auth-token': token } }
        );
        toast.success('User added successfully');
      }
      
      navigate('/admin/users');
    } catch (err) {
      console.error('Error saving user:', err);
      setError(err.response?.data?.error || 'Failed to save user');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="user-form-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>{isEdit ? 'Edit User' : 'Add New User'}</h2>
        <Button 
          variant="secondary" 
          onClick={() => navigate('/admin/users')}
        >
          <FaArrowLeft className="me-2" />
          Back to Users
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Full Name</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaUser />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      name="name"
                      value={user.name}
                      onChange={handleChange}
                      placeholder="Enter user's full name"
                      required
                    />
                  </InputGroup>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email Address</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaEnvelope />
                    </InputGroup.Text>
                    <Form.Control
                      type="email"
                      name="email"
                      value={user.email}
                      onChange={handleChange}
                      placeholder="Enter email address"
                      required
                      disabled={isEdit} // Don't allow changing email for existing users
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaPhone />
                    </InputGroup.Text>
                    <Form.Control
                      type="tel"
                      name="phone"
                      value={user.phone}
                      onChange={handleChange}
                      placeholder="Enter phone number"
                    />
                  </InputGroup>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>User Type</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaShieldAlt />
                    </InputGroup.Text>
                    <Form.Select
                      name="usertype"
                      value={user.usertype}
                      onChange={handleChange}
                      required
                    >
                      <option value="customer">Customer</option>
                      
                    </Form.Select>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end">
              <Button 
                variant="primary" 
                type="submit" 
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FaSave className="me-2" />
                    {isEdit ? 'Update User' : 'Add User'}
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default AddEditUser;